<template>
  <div>
    <v-divider v-if="!hideTopDivider" />
    <div class="scrollable-container">
      <v-data-table
        v-model:sort-by="sortingBy"
        :headers="headers"
        :items="items"
        :items-per-page-text="footerOptions['items-per-page-text']"
        :items-per-page-options="footerOptions['items-per-page-options']"
        :show-first-last-page="footerOptions['show-first-last-page']"
        :loading="isLoading"
        :loading-text="t('loading')"
        :no-data-text="t('no_data_available')"
        :single-expand="true"
        :items-per-page="itemsPerPageDefault"
        :page="currentPage"
        checkbox-color="primary"
        :show-select="false"
        :hide-default-footer="disablePagination"
        show-expand
        @update:page="updateCurrentPage($event)"
      >
        <template #header.data-table-select>
          <v-checkbox
            hide-details
            class="mt-0 pa-0"
          />
        </template>
        <template #item.count="{ value }">
          <div class="d-flex align-center">
            {{ value }}
            <v-tooltip
              :text="t('station_table.combined_stations_tooltip')"
              :open-delay="200"
              right
              :nudge-bottom="35"
              content-class="station-hint"
              data-testid="tooltip-combined-stations-simple"
            >
              <template #activator="{ props }">
                <v-icon
                  v-bind="props"
                  data-testid="tooltip-combined-stations-simple-icon"
                  :style="value <= 1 && { visibility: 'hidden' }"
                  color="primary"
                  size="x-small"
                >
                  mdi-help
                </v-icon>
              </template>
            </v-tooltip>
          </div>
        </template>
        <template #item.startDate="{ value }">
          {{ cleanDate(value) }}
        </template>
        <template #item.endDate="{ value }">
          {{ cleanDate(value) }}
        </template>
        <template #item.state="{ value }">
          {{ cleanState(value) }}
        </template>
        <template #item.hasSunshine="{ value }">
          {{ cleanBoolean(value) }}
        </template>
        <template #item.hasGlobalRadiation="{ value }">
          {{ cleanBoolean(value) }}
        </template>
        <template #item.data-table-expand="{ internalItem, isExpanded, toggleExpand }">
          <v-btn
            icon
            variant="flat"
            :style="internalItem.raw.type !== 'COMBINED' && { visibility: 'hidden' }"
            @click="toggleExpand(internalItem)"
          >
            <v-icon :icon="isExpanded(internalItem) ? 'mdi-chevron-up' : 'mdi-chevron-down'" />
          </v-btn>
        </template>
        <template #expanded-row="{ item }">
          <tr class="expanded-row">
            <td 
              :colspan="1"
            />
            <td
              v-for="key in headerNames"
              :key="key"
              class="pa-0"
            >
              <div class="d-flex flex-column">
                <div
                  v-for="station in item.subStations"
                  :key="station.id"
                  :class="
                    'substation-row d-flex px-4 align-center' +
                      (stationIsDisabled(station) ? ' zamg-text-disabled' : '')
                  "
                >
                  {{ cleanSubstationValue(station[key]) }}
                  <v-tooltip
                    v-if="stationIsDisabled(station) && key === 'count'"
                    :text="`${station.name} (${station.id}) ${t(
                      'station_table.combined_station_not_matched'
                    )}`"
                    :open-delay="200"
                    right
                    :nudge-bottom="35"
                    content-class="station-hint"
                  >
                    <template #activator="{ props }">
                      <v-icon
                        v-bind="props"
                        color="primary"
                        size="x-small"
                      >
                        mdi-help
                      </v-icon>
                    </template>
                  </v-tooltip>
                </div>
              </div>
            </td>
          </tr>
        </template>
        <template #footer.page-text="item">
          {{ `${item.pageStart} - ${item.pageStop} ${t('of')} ${items.length}` }}
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script lang="ts">
import { Station } from "@/stores/station/station-model";
import { defineComponent, ref, computed, watch, PropType } from "vue";
import type { DataTableHeader } from "vuetify";
import { localeDateString, getDisplayFrequency } from "@/util/datetime";
import DatasetStore from "@/stores/dataset/dataset-module";
import { useI18n } from "vue-i18n";
import { Bundesland } from '@/api/out';

type BundeslandKey = keyof typeof Bundesland;

interface FooterOptions {
  'items-per-page-options': number[];
  'items-per-page-text': string;
  'show-first-last-page': boolean;
}

export default defineComponent({
  name: 'SimpleStationTable',
  props: {
    items: { type: Array as PropType<Station[]>, required: true },
    isLoading: { type: Boolean, required: true },
    disablePagination: { type: Boolean, default: false },
    greyOutUnmatchedSubstations: { type: Boolean, default: true },
    hiddenHeaderNames: { type: Array as PropType<string[]>, default: () => [] },
    hideTopDivider: { type: Boolean, default: false },
  },
  setup(props) {
    const { t, locale } = useI18n();

    const currentPage = ref(1);
    const itemsPerPageDefault = props.disablePagination ? -1 : 6;
    const sortingBy = ref(["id"]);
    const datasetStore = DatasetStore;

    watch(props.items, () => {
      // reset page when items of table change
      currentPage.value = 1;
    });

    const footerOptions: FooterOptions = {
      'items-per-page-options': [2, 4, 6, 8, 12, 16],
      'items-per-page-text': t('table.rows_per_page').toString(),
      'show-first-last-page': true,
    };

    const defaultHeaders: Array<keyof Station> = [
      'count',
      'id',
      'name',
      'startDate',
      'endDate',
      'state',
      'altitude',
      'hasSunshine',
      'hasGlobalRadiation',
    ];

    const headerNames = computed<Array<keyof Station>>(() => {
      return defaultHeaders.filter(
        (name) => !props.hiddenHeaderNames.includes(name as string)
      );
    });

    const extraHeaders: string[] = ["data-table-expand"];

    const headers = computed<DataTableHeader[]>(() => {
      const defaultProps: Partial<DataTableHeader> = {
        headerProps: {'class': 'zamg-text-continuous zamg-text-input'},
        align: 'start',
        sortable: false,
      };
      const propsForExtra: Partial<DataTableHeader> = {
        ...defaultProps,
        sortable: false,
        width: "5%",
      };
      return extraHeaders
        .map((name) => ({
          ...propsForExtra,
          title: "",
          key: name,
        }))
        .concat(
          headerNames.value
            .map((name) => ({
              ...defaultProps,
              title: t(`station_table.table.${name}`).toString(),
              key: name,
            }))
        );
    });

    function stationIsDisabled(station: Station): boolean {
      return station.matchesFilter === false && props.greyOutUnmatchedSubstations;
    }

    function updateCurrentPage(newPage: number) {
      currentPage.value = newPage;
    }

    function cleanDate(date: Date): string {
      if (datasetStore.metadata) {
        return localeDateString(
          date,
          locale.value,
          getDisplayFrequency(datasetStore.metadata.frequency)
        );
      }
      return '';
    }

    const cleanState = (state: string): string => {
      const key = (Object.keys(Bundesland) as Array<BundeslandKey>)
        .find(k => Bundesland[k] === state);

      return key ? t(`state.${String(key)}`) : state;
    }

    function cleanBoolean(value: boolean): string {
      return value ? t('true').toString() : t('false').toString();
    }

    function cleanSubstationValue(value: string | boolean | Date): string {
      if (value instanceof Date) {
        return cleanDate(value);
      }
      if (typeof value === 'boolean') {
        return cleanBoolean(value);
      }
      return value;
    }

    return {
      t,
      currentPage,
      sortingBy,
      itemsPerPageDefault,
      footerOptions,
      headerNames,
      headers,
      stationIsDisabled,
      updateCurrentPage,
      cleanDate,
      cleanState,
      cleanBoolean,
      cleanSubstationValue,
    };
  },
});
</script>

<style scoped>
:deep(.v-data-table-footer__select) {
  /*put pagination of table to the left*/
  margin-left: 0 !important;
}

:deep(.v-data-table__tr) > td {
  /*Switch default border from bottom to top, needed to hide border when expanding a row*/
  border-bottom: 0 !important;
  border-top: 1px solid rgb(var(--v-theme-divider));
}

.expanded-row > td {
  /*Hide border between substations and next main station when expanding a row*/
  border-bottom: 0 !important;
}

.substation-row {
  height: 2rem;
  font-size: 0.75rem;
}
</style>
